import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const SourceBadge = ({ source }) => {
  const map = {
    waha: { color: '#34D399', bg: 'rgba(52,211,153,0.12)', label: '💬 WhatsApp' },
    instagram: { color: '#F472B6', bg: 'rgba(244,114,182,0.12)', label: '📷 Instagram' },
    elevenlabs: { color: '#60A5FA', bg: 'rgba(96,165,250,0.12)', label: '🎙️ ElevenLabs' },
    n8n: { color: '#FBBF24', bg: 'rgba(251,191,36,0.12)', label: '🔁 n8n' },
    other: { color: '#9CA3AF', bg: 'rgba(156,163,175,0.12)', label: 'Diğer' }
  };
  const s = map[source] || map.other;
  return (
    <span style={{
      background: s.bg, color: s.color, border: `1px solid ${s.color}33`,
      padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap'
    }}>{s.label}</span>
  );
};

const StatusChip = ({ code }) => {
  if (code == null) return <span style={{ color: '#6B7280' }}>-</span>;
  const isError = code >= 400;
  return (
    <span style={{
      fontFamily: 'monospace', fontWeight: 700, fontSize: 13,
      color: isError ? '#F87171' : '#34D399'
    }}>{code}</span>
  );
};

const SignatureChip = ({ valid }) => {
  if (valid === null || valid === undefined) return <span style={{ color: '#6B7280' }}>–</span>;
  return valid ? <span style={{ color: '#34D399' }}>✓</span> : <span style={{ color: '#F87171' }}>✗</span>;
};

const WebhookLogsTab = ({ token }) => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ source: 'all', status: 'all', branch_id: '', q: '' });
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const limit = 50;
  const headers = { Authorization: `Bearer ${token}` };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.source !== 'all') params.append('source', filters.source);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.branch_id) params.append('branch_id', filters.branch_id);
      if (filters.q) params.append('q', filters.q);
      params.append('page', page);
      params.append('limit', limit);

      const res = await axios.get(`/api/admin/webhook-logs?${params}`, { headers });
      setLogs(res.data.data.logs || []);
      setTotal(res.data.data.total || 0);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [filters, page, token]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => {
    if (selectedId == null) { setDetail(null); return; }
    setDetailLoading(true);
    axios.get(`/api/admin/webhook-logs/${selectedId}`, { headers })
      .then(res => setDetail(res.data.data))
      .catch(err => console.error(err))
      .finally(() => setDetailLoading(false));
  }, [selectedId]);

  const handleCleanup = async () => {
    const days = window.prompt('Kaç günden eski kayıtlar silinsin?', '30');
    if (!days) return;
    if (!window.confirm(`${days} günden eski tüm webhook kayıtları silinecek. Emin misiniz?`)) return;
    try {
      const res = await axios.delete(`/api/admin/webhook-logs?before_days=${encodeURIComponent(days)}`, { headers });
      alert(`${res.data.deleted} kayıt silindi.`);
      fetchLogs();
    } catch (err) {
      alert(err.response?.data?.error?.message || 'Silme işlemi başarısız oldu');
    }
  };

  const formatDate = (d) => new Date(d).toLocaleString('tr-TR');
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="wh-logs-tab">
      <div className="wh-logs-header">
        <div>
          <h2>🪝 Webhook Logları</h2>
          <p style={{ margin: 0, color: '#6B7280', fontSize: 13 }}>{total} kayıt (son 24 saat varsayılan aralık)</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn-refresh" onClick={fetchLogs}>🔄 Yenile</button>
          <button className="btn-refresh" onClick={handleCleanup}>🗑️ Eski Kayıtları Temizle</button>
        </div>
      </div>

      <div className="filters-row">
        <div className="search-wrap">
          <span className="search-icon">🔍</span>
          <input
            type="text" className="search-input"
            placeholder="Endpoint veya içerikte ara..."
            value={filters.q}
            onChange={e => { setPage(1); setFilters({ ...filters, q: e.target.value }); }}
          />
        </div>
        <select className="filter-select" value={filters.source} onChange={e => { setPage(1); setFilters({ ...filters, source: e.target.value }); }}>
          <option value="all">Tüm Kaynaklar</option>
          <option value="waha">💬 WhatsApp</option>
          <option value="instagram">📷 Instagram</option>
          <option value="elevenlabs">🎙️ ElevenLabs</option>
          <option value="n8n">🔁 n8n</option>
          <option value="other">Diğer</option>
        </select>
        <select className="filter-select" value={filters.status} onChange={e => { setPage(1); setFilters({ ...filters, status: e.target.value }); }}>
          <option value="all">Tüm Durumlar</option>
          <option value="ok">Başarılı</option>
          <option value="error">Hatalı (4xx/5xx)</option>
        </select>
        <input
          type="number" className="filter-select" style={{ width: 110 }}
          placeholder="Şube ID"
          value={filters.branch_id}
          onChange={e => { setPage(1); setFilters({ ...filters, branch_id: e.target.value }); }}
        />
      </div>

      <div className="calls-table-wrap">
        <table className="calls-table">
          <thead>
            <tr>
              <th>Zaman</th>
              <th>Kaynak</th>
              <th>Method / Path</th>
              <th>Şube</th>
              <th>Durum</th>
              <th>Süre</th>
              <th>İmza</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="table-empty"><div className="mini-spinner" /> Yükleniyor...</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan="7" className="table-empty">🔍 Kayıt bulunamadı</td></tr>
            ) : logs.map(log => (
              <tr
                key={log.id}
                className="calls-row"
                onClick={() => setSelectedId(log.id)}
                style={log.status_code >= 400 ? { background: 'rgba(248,113,113,0.06)' } : undefined}
              >
                <td className="cell-muted">{formatDate(log.created_at)}</td>
                <td><SourceBadge source={log.source} /></td>
                <td>
                  <div className="cell-mono" style={{ fontSize: 12 }}>{log.method} {log.endpoint}</div>
                  {log.error_message && <div style={{ fontSize: 11, color: '#F87171', marginTop: 2 }}>{log.error_message}</div>}
                </td>
                <td className="cell-muted">{log.branch_id || '-'}</td>
                <td><StatusChip code={log.status_code} /></td>
                <td className="cell-mono">{log.response_time_ms != null ? `${log.response_time_ms}ms` : '-'}</td>
                <td><SignatureChip valid={log.signature_valid} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, alignItems: 'center' }}>
          <button className="btn-refresh" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Önceki</button>
          <span style={{ color: '#9CA3AF', fontSize: 13 }}>{page} / {totalPages}</span>
          <button className="btn-refresh" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Sonraki ›</button>
        </div>
      )}

      {selectedId != null && (
        <div className="modal-overlay" onClick={() => setSelectedId(null)}>
          <div className="calls-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 780 }}>
            <div className="modal-hdr">
              <div>
                <h3 style={{ margin: 0, color: '#F9FAFB' }}>🪝 Webhook Detayı</h3>
                {detail && <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>{formatDate(detail.created_at)}</div>}
              </div>
              <button className="modal-x" onClick={() => setSelectedId(null)}>×</button>
            </div>
            <div className="modal-bdy">
              {detailLoading || !detail ? (
                <div className="table-empty"><div className="mini-spinner" /> Yükleniyor...</div>
              ) : (
                <>
                  <div className="detail-grid-2">
                    <div className="detail-cell"><label>Kaynak</label><div style={{ marginTop: 4 }}><SourceBadge source={detail.source} /></div></div>
                    <div className="detail-cell"><label>Durum</label><div className="detail-val"><StatusChip code={detail.status_code} /></div></div>
                    <div className="detail-cell"><label>Method / Path</label><div className="detail-val mono" style={{ fontSize: 13 }}>{detail.method} {detail.endpoint}</div></div>
                    <div className="detail-cell"><label>Şube</label><div className="detail-val">{detail.branch_id || '-'}</div></div>
                    <div className="detail-cell"><label>Süre</label><div className="detail-val mono">{detail.response_time_ms != null ? `${detail.response_time_ms}ms` : '-'}</div></div>
                    <div className="detail-cell"><label>İmza</label><div className="detail-val"><SignatureChip valid={detail.signature_valid} /></div></div>
                  </div>

                  {detail.error_message && (
                    <div className="info-box" style={{ background: 'rgba(248,113,113,0.07)', border: '1px solid rgba(248,113,113,0.2)' }}>
                      <label style={{ color: '#F87171' }}>⚠️ Hata</label>
                      <div>{detail.error_message}</div>
                    </div>
                  )}

                  <div className="transcript-box">
                    <label>📥 İstek Gövdesi (request_body)</label>
                    <pre>{JSON.stringify(detail.request_body, null, 2)}</pre>
                  </div>

                  <div className="transcript-box">
                    <label>📤 Cevap Gövdesi (response_body)</label>
                    <pre>{JSON.stringify(detail.response_body, null, 2)}</pre>
                  </div>

                  <div className="transcript-box">
                    <label>🧾 Header'lar</label>
                    <pre>{JSON.stringify(detail.request_headers, null, 2)}</pre>
                  </div>

                  {detail.query_params && Object.keys(detail.query_params).length > 0 && (
                    <div className="transcript-box">
                      <label>🔗 Query Parametreleri</label>
                      <pre>{JSON.stringify(detail.query_params, null, 2)}</pre>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .wh-logs-tab { display:flex; flex-direction:column; gap:20px; }

        .wh-logs-header { display:flex; justify-content:space-between; align-items:flex-start; }
        .wh-logs-header h2 { margin:0 0 2px; font-size:22px; font-weight:700; color:#F9FAFB; }
        .btn-refresh { display:flex; align-items:center; gap:8px; padding:10px 18px; background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.08); border-radius:10px; color:#E5E7EB; font-size:14px; cursor:pointer; transition:all 0.2s; }
        .btn-refresh:hover { background:rgba(255,255,255,0.1); }
        .btn-refresh:disabled { opacity:0.4; cursor:default; }

        .filters-row { display:flex; gap:12px; flex-wrap:wrap; }
        .search-wrap { position:relative; flex:1; min-width:200px; }
        .search-icon { position:absolute; left:12px; top:50%; transform:translateY(-50%); font-size:14px; }
        .search-input { width:100%; padding:10px 14px 10px 36px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.08); border-radius:10px; color:#E5E7EB; font-size:14px; outline:none; transition:border-color 0.2s; box-sizing:border-box; }
        .search-input:focus { border-color:rgba(96,165,250,0.4); }
        .filter-select { padding:10px 14px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.08); border-radius:10px; color:#E5E7EB; font-size:14px; outline:none; cursor:pointer; }
        .filter-select option { background:#13131a; }

        .calls-table-wrap { background:rgba(17,17,24,0.7); border:1px solid rgba(255,255,255,0.07); border-radius:16px; overflow:hidden; backdrop-filter:blur(16px); overflow-x:auto; }
        .calls-table { width:100%; border-collapse:collapse; }
        .calls-table thead th { padding:12px 16px; text-align:left; font-size:11px; text-transform:uppercase; letter-spacing:0.06em; color:#6B7280; font-weight:700; background:rgba(255,255,255,0.03); border-bottom:1px solid rgba(255,255,255,0.06); white-space:nowrap; }
        .calls-row { cursor:pointer; transition:background 0.15s; }
        .calls-row:hover { background:rgba(255,255,255,0.04); }
        .calls-row td { padding:14px 16px; border-bottom:1px solid rgba(255,255,255,0.04); vertical-align:middle; }
        .calls-row:last-child td { border-bottom:none; }
        .cell-muted { font-size:13px; color:#6B7280; }
        .cell-mono { font-family:monospace; font-size:13px; color:#E5E7EB; }
        .table-empty { text-align:center; padding:48px; color:#6B7280; font-size:14px; }
        .mini-spinner { display:inline-block; width:16px; height:16px; border:2px solid rgba(255,255,255,0.1); border-top-color:#60A5FA; border-radius:50%; animation:spin 0.8s linear infinite; margin-right:8px; vertical-align:middle; }
        @keyframes spin { to { transform:rotate(360deg); } }

        .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.7); backdrop-filter:blur(8px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
        .calls-modal { background:#0f0f16; border:1px solid rgba(255,255,255,0.1); border-radius:20px; width:100%; max-height:85vh; overflow:hidden; display:flex; flex-direction:column; box-shadow:0 24px 60px rgba(0,0,0,0.6); }
        .modal-hdr { display:flex; justify-content:space-between; align-items:flex-start; padding:20px 24px; border-bottom:1px solid rgba(255,255,255,0.07); }
        .modal-x { background:none; border:none; font-size:28px; color:#6B7280; cursor:pointer; line-height:1; transition:color 0.15s; }
        .modal-x:hover { color:#F87171; }
        .modal-bdy { padding:24px; overflow-y:auto; display:flex; flex-direction:column; gap:16px; }

        .detail-grid-2 { display:grid; grid-template-columns:repeat(2,1fr); gap:12px; }
        .detail-cell { background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.06); border-radius:10px; padding:12px 14px; }
        .detail-cell label { font-size:11px; text-transform:uppercase; letter-spacing:0.06em; color:#6B7280; display:block; margin-bottom:6px; }
        .detail-val { font-size:15px; font-weight:600; color:#E5E7EB; }
        .detail-val.mono { font-family:monospace; }

        .info-box { border-radius:12px; padding:16px; }
        .info-box label { font-size:12px; font-weight:600; display:block; margin-bottom:8px; }
        .info-box div { font-size:14px; color:#D1D5DB; line-height:1.6; }

        .transcript-box { background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.07); border-radius:12px; padding:16px; }
        .transcript-box label { font-size:12px; font-weight:600; color:#9CA3AF; display:block; margin-bottom:8px; }
        .transcript-box pre { margin:0; white-space:pre-wrap; word-break:break-word; font-size:12px; color:#9CA3AF; line-height:1.6; font-family:monospace; max-height:300px; overflow-y:auto; }
      `}</style>
    </div>
  );
};

export default WebhookLogsTab;
